import { useCallback, useState } from 'react'
import { FetchPrescriptiveModel, ModelInputTag, TagDto } from 'src/services'
import { Button, Card, Text } from 'src/components/ui'
import { useSite } from 'src/contexts/site'
import { SiteRole } from 'src/types'
import { AddInputTagsModal } from 'models/create'
import { mapModelToModelRecord } from 'models/model/model.utils'
import { useAddInputTagMutation, useRemoveInputTagMutation } from 'models/api'
import { InputTagsList } from './components'
type Props = {
  model: FetchPrescriptiveModel
  inputTags: ModelInputTag[]
  tags: TagDto[]
  disabled: boolean
}

export function InputTags({
  model,
  inputTags,
  tags,
  disabled,
}: Props): JSX.Element {
  const [isInputTagsModalOpen, setIsInputTagsModalOpen] = useState(false)
  const { viewerRole } = useSite()

  const addInputTag = useAddInputTagMutation()
  const removeInputTag = useRemoveInputTagMutation()

  const handleAddInputTag = useCallback(
    async (tagName: string): Promise<void> => {
      const tag = tags.find(t => t.tagName === tagName)
      if (!tag) return
      await addInputTag.mutateAsync({
        modelId: model.id,
        tagId: tag.tagNodeId,
      })
    },
    [addInputTag, model.id, tags],
  )

  const handleRemoveInputTag = useCallback(
    async (tagName: string): Promise<void> => {
      const tag = tags.find(t => t.tagName === tagName)
      if (!tag) return
      await removeInputTag.mutateAsync({
        modelId: model.id,
        tagId: tag.tagNodeId,
      })
    },
    [tags, removeInputTag, model.id],
  )

  return (
    <div className="col-span-2 flex flex-col gap-s">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2xs">
          <Text variant="title" bold>
            Input Tags
          </Text>
        </div>
        {viewerRole !== SiteRole.READER && !disabled && (
          <Button
            variant="secondary"
            title="Add Input Tags"
            onClick={() => setIsInputTagsModalOpen(true)}
          />
        )}
      </div>
      <Card className="p-0">
        <InputTagsList
          inputTags={inputTags}
          model={model}
          isEditable={!disabled && viewerRole !== SiteRole.READER}
          onRemoveInputTag={handleRemoveInputTag}
        />
        <AddInputTagsModal
          inputTags={inputTags.map(t => t.node.tagName)}
          isOpen={isInputTagsModalOpen}
          setIsOpen={setIsInputTagsModalOpen}
          model={mapModelToModelRecord(model)}
          addInputTag={handleAddInputTag}
          removeInputTag={handleRemoveInputTag}
          isError={addInputTag.isError || removeInputTag.isError}
          disabled={addInputTag.isLoading || removeInputTag.isLoading}
          disabledTags={[model.tag.tagName]}
        />
      </Card>
    </div>
  )
}
